import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { showSuccess, showError } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { attendanceService } from '@/services/attendanceService'
import { employeesService } from '@/services/employeesService'
import { shiftService } from '@/services/shiftService'
import type { Employee } from '@/api/types'
import type { DepartmentLookup } from '@/api/types'
import type { ShiftListItem } from '@/api/types'

function toUTCISO(dateStr: string, timeHHmm: string): string {
  if (!dateStr || !timeHHmm || timeHHmm.length < 5) return ''
  const local = `${dateStr}T${timeHHmm}:00`
  return new Date(local).toISOString()
}

/** Get HH:mm from shift start/end (supports "09:00", "09:00:00", or ISO string). Uses local time so shift 9–5 shows as 09:00–17:00. */
function shiftTimeToHHmm(value?: string | null): string {
  if (!value || typeof value !== 'string') return ''
  const trimmed = value.trim()
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) return trimmed.slice(0, 5)
  try {
    const d = new Date(trimmed)
    if (!Number.isNaN(d.getTime())) {
      const h = d.getHours()
      const m = d.getMinutes()
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    }
  } catch (_) {}
  return ''
}

export const AttendanceAddManual: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [saving, setSaving] = useState(false)
  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [employeesList, setEmployeesList] = useState<Employee[]>([])
  const [shifts, setShifts] = useState<ShiftListItem[]>([])

  const [manualDeptId, setManualDeptId] = useState(0)
  const [manualEmpId, setManualEmpId] = useState(0)
  const [manualShiftId, setManualShiftId] = useState<number>(0)
  const [manualStatus, setManualStatus] = useState(1)
  const [manualDate, setManualDate] = useState(() => new Date().toISOString().slice(0, 10))
  const [manualTimeIn, setManualTimeIn] = useState('09:00')
  const [manualTimeOut, setManualTimeOut] = useState('17:00')
  const [manualWorkingHours, setManualWorkingHours] = useState(8)
  const [manualLateHours, setManualLateHours] = useState(0)
  const [manualIncompleteHours, setManualIncompleteHours] = useState(0)
  const [manualDescription, setManualDescription] = useState('')

  useEffect(() => {
    if (!branchId) {
      setDepartments([])
      return
    }
    let cancelled = false
    employeesService.getDepartmentLookups().then((list) => {
      if (!cancelled && list) setDepartments(list)
    }).catch(() => { if (!cancelled) setDepartments([]) })
    return () => { cancelled = true }
  }, [branchId])

  useEffect(() => {
    if (!branchId) {
      setEmployeesList([])
      return
    }
    let cancelled = false
    employeesService.getAllEmployees(branchId, 1, 500).then((res) => {
      if (!cancelled && res?.data) setEmployeesList(res.data)
    }).catch(() => { if (!cancelled) setEmployeesList([]) })
    return () => { cancelled = true }
  }, [branchId])

  useEffect(() => {
    if (!branchId) {
      setShifts([])
      return
    }
    let cancelled = false
    shiftService.getAll(branchId, 1, 200).then((res) => {
      if (!cancelled && res?.data) setShifts(res.data)
    }).catch(() => { if (!cancelled) setShifts([]) })
    return () => { cancelled = true }
  }, [branchId])

  const departmentOptionsForCombined = useMemo(() =>
    departments.filter((d) => d.id).map((d) => ({
      value: String(d.id),
      label: d.lookupNameEn || d.lookupNameAr || String(d.id),
    })), [departments])

  const manualEmployeeOptions = useMemo(() => {
    const nameFor = (emp: Employee) =>
      [emp.empFirstNameEn, emp.empFamilyNameEn].filter(Boolean).join(' ').trim() || emp.empCode || '—'
    if (!manualDeptId) {
      return employeesList.filter((e) => e.id && e.empCode).map((e) => ({ value: String(e.id), label: nameFor(e) }))
    }
    return employeesList
      .filter((e) => e.empDepartmetId === manualDeptId && e.id && e.empCode)
      .map((e) => ({ value: String(e.id), label: nameFor(e) }))
  }, [employeesList, manualDeptId])

  useEffect(() => {
    if (!manualEmpId) return
    const emp = employeesList.find((e) => e.id === manualEmpId) as (Employee & { shiftId?: number }) | undefined
    if (emp?.shiftId) setManualShiftId(emp.shiftId)
  }, [manualEmpId, employeesList])

  useEffect(() => {
    if (!manualShiftId || shifts.length === 0) return
    const shift = shifts.find((s) => s.id === manualShiftId)
    if (!shift) return
    const start = shiftTimeToHHmm(shift.startTime ?? shift.shiftStartTime)
    const end = shiftTimeToHHmm(shift.endTime ?? shift.shiftEndTime)
    if (start) setManualTimeIn(start)
    if (end) setManualTimeOut(end)
  }, [manualShiftId, shifts])

  const shiftOptions = useMemo(() =>
    shifts.filter((s) => s.id != null).map((s) => ({
      value: String(s.id),
      label: s.name ?? s.shiftType ?? s.shiftDescription ?? s.description ?? t('attendancePage.shiftFallback', { id: s.id }),
    })), [shifts, t])

  const handleSave = async () => {
    if (!branchId) {
      toast.error('Select a branch.')
      return
    }
    if (!manualEmpId) {
      toast.error('Select an employee.')
      return
    }
    const dateStr = manualDate || new Date().toISOString().slice(0, 10)
    const timeInISO = toUTCISO(dateStr, manualTimeIn) || new Date().toISOString()
    const timeOutISO = toUTCISO(dateStr, manualTimeOut) || new Date().toISOString()

    try {
      const existing = await attendanceService.getListByFilters(branchId, {
        departmentId: 0,
        empId: manualEmpId,
        dateFrom: dateStr,
        dateTo: dateStr,
        pageNumber: 1,
        pageSize: 10,
      })
      if (existing.data && existing.data.length > 0) {
        await showError(t('attendancePage.validationDupTitle'), t('attendancePage.validationDupMessage'))
        return
      }
    } catch (_) {
      // proceed with save if check fails (e.g. network)
    }

    setSaving(true)
    try {
      const payload = {
        employeeId: manualEmpId,
        attendanceStatus: manualStatus,
        date: dateStr,
        timeIn: timeInISO,
        timeOut: timeOutISO,
        workingHours: manualWorkingHours,
        lateHours: manualLateHours,
        incompleteHours: manualIncompleteHours,
        branchId,
        description: manualDescription || undefined,
      }
      console.log('[Attendance Manual] API payload:', JSON.stringify(payload, null, 2))
      await attendanceService.addAttendance(payload)
      await showSuccess(t('attendancePage.savedTitle'), t('attendancePage.savedMessage'))
      navigate('/payroll/attendance')
    } catch (e: any) {
      await showError(t('attendancePage.saveFailedTitle'), e?.response?.data?.message ?? e?.message ?? t('attendancePage.saveFailedMessage'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('attendancePage.manualTitle')}
        description={t('attendancePage.manualDescription')}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('attendancePage.detailCardTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>{t('attendancePage.department')}</Label>
              <SearchableSelect
                options={[
                  { value: '0', label: t('attendancePage.allDepartments') },
                  ...departmentOptionsForCombined,
                ]}
                value={manualDeptId ? String(manualDeptId) : '0'}
                onValueChange={(v) => { setManualDeptId(v ? Number(v) : 0); setManualEmpId(0) }}
                placeholder={t('attendancePage.placeholderSelectDepartment')}
                searchPlaceholder={t('attendancePage.searchShort')}
                emptyMessage={t('attendancePage.emptyDeptShort')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.employee')}</Label>
              <SearchableSelect
                options={manualEmployeeOptions}
                value={manualEmpId ? String(manualEmpId) : ''}
                onValueChange={(v) => setManualEmpId(v ? Number(v) : 0)}
                placeholder={t('attendancePage.placeholderSelectEmployee')}
                searchPlaceholder={t('attendancePage.searchShort')}
                emptyMessage={t('attendancePage.emptyEmpShort')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.labelShift')}</Label>
              <SearchableSelect
                options={shiftOptions}
                value={manualShiftId ? String(manualShiftId) : ''}
                onValueChange={(v) => setManualShiftId(v ? Number(v) : 0)}
                placeholder={t('attendancePage.placeholderSelectShift')}
                searchPlaceholder={t('attendancePage.searchShort')}
                emptyMessage={t('attendancePage.emptyShift')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.labelStatus')}</Label>
              <Select value={String(manualStatus)} onValueChange={(v) => setManualStatus(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">{t('attendancePage.present')}</SelectItem>
                  <SelectItem value="0">{t('attendancePage.absent')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.labelDate')}</Label>
              <Input
                type="date"
                value={manualDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setManualDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.labelTimeIn')}</Label>
              <Input type="time" value={manualTimeIn} onChange={(e) => setManualTimeIn(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.labelTimeOut')}</Label>
              <Input type="time" value={manualTimeOut} onChange={(e) => setManualTimeOut(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.labelWorkingHours')}</Label>
              <Input type="number" value={manualWorkingHours} onChange={(e) => setManualWorkingHours(Number(e.target.value) || 0)} />
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.labelLateHours')}</Label>
              <Input type="number" value={manualLateHours} onChange={(e) => setManualLateHours(Number(e.target.value) || 0)} />
            </div>
            <div className="space-y-2">
              <Label>{t('attendancePage.labelIncompleteHours')}</Label>
              <Input type="number" value={manualIncompleteHours} onChange={(e) => setManualIncompleteHours(Number(e.target.value) || 0)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t('attendancePage.labelDescription')}</Label>
            <Textarea
              placeholder={t('attendancePage.descriptionOptional')}
              value={manualDescription}
              onChange={(e) => setManualDescription(e.target.value)}
              rows={3}
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? t('attendancePage.saving') : t('attendancePage.save')}
            </Button>
            <Button variant="outline" onClick={() => navigate('/payroll/attendance')}>
              {t('attendancePage.cancel')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
